import Cookies from 'js-cookie';

import Button from '~/components/Button';
import { FacebookIcon } from '~/components/icons/components/FacebookIcon';
import { useAlert } from '~/contexts/alert';
import {
  AlertError,
  getAlertFeedBackError,
} from '~/contexts/alert/alertFeedBackError';
import { captureException } from '~/errorsLogging';
const POLLING_INTERVAL = 500; // Check every 500ms
const MAX_POLLING_ATTEMPTS = 500; // Max 250 seconds of polling

interface FacebookAuthButtonProps {
  facebookAuthUrl?: string | AlertError;
  trackId: string;
}

const FacebookAuthButton: React.FC<FacebookAuthButtonProps> = ({
  facebookAuthUrl,
  trackId,
}) => {
  const { setAlertProps } = useAlert();

  // Helper function to fire an internal message event
  const fireInternalPostMessage = (authEvent: any) => {
    try {
      // Dispatches a MessageEvent to the window, which is picked up by the
      // LoginScreen.container.tsx useEffect listener.
      const messageEvent = new MessageEvent('message', {
        data: authEvent,
        origin: window.location.origin,
      });
      window.dispatchEvent(messageEvent);
    } catch (e) {
      captureException(new Error('Failed to fire internal postMessage'));
    }
  };

  const handlePolling = () => {
    let pollingAttempts = 0;

    const poller = setInterval(() => {
      pollingAttempts += 1;
      if (pollingAttempts > MAX_POLLING_ATTEMPTS) {
        clearInterval(poller);

        return;
      }

      const authCookie = Cookies.get('sso_auth_result');

      if (authCookie) {
        clearInterval(poller);
        // Parse and fire the message
        try {
          const authData = JSON.parse(authCookie);
          fireInternalPostMessage(authData);
        } catch (e) {
          fireInternalPostMessage({
            type: 'authservice-sso-login-error',
            message: 'Failed to parse auth data',
          });
        }

        Cookies.remove('sso_auth_result', {
          path: '/',
          domain: '.usefoam.com',
          secure: true,
        });

        return;
      }
    }, POLLING_INTERVAL);
  };

  const handleOnClick = (): void => {
    if (typeof facebookAuthUrl === 'string') {
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2.5;
      const title = 'Authenticate with Facebook';
      const url = facebookAuthUrl;

      // Open the popup and store the reference
      const popup = window.open(
        url,
        title,
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (popup) {
        // Start the polling immediately
        handlePolling();
      } else {
        // Handle popup blocked case
        getAlertFeedBackError(
          {
            message: 'Popup blocked',
            errorCode: 500,
          },
          setAlertProps
        );
      }
    } else {
      getAlertFeedBackError(
        {
          message: 'Facebook auth URL is not a string',
          errorCode: 500,
        },
        setAlertProps
      );
    }
  };

  if (!facebookAuthUrl) {
    return null;
  }

  return (
    <Button
      marginBottom={4}
      width="100%"
      fontSize="md"
      size="lg"
      background="white"
      border="1px solid #1c2128"
      line-height="10px"
      textTransform="none"
      leftIcon={<FacebookIcon boxSize={6} />}
      onClick={handleOnClick}
      track-id={trackId}
    >
      Continue with Facebook
    </Button>
  );
};

export default FacebookAuthButton;
